"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { WorkshopTag } from "@/components/Craft";
import type { Project } from "@/lib/projects";
import { d, size } from "@/lib/site";
import "./wand.css";

const sparkColors = ["#fde68a", "#fcd34d", "#fbbf24", "#fff7ed"];

// Mouse pointer over the photo: a wand with a glowing star tip; the click point is the star's centre (9 9).
const wandSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
<circle cx="9" cy="9" r="8" fill="#fde68a" opacity=".35"/>
<line x1="14" y1="14" x2="36" y2="36" stroke="#1c1917" stroke-width="5" stroke-linecap="round"/>
<line x1="14" y1="14" x2="36" y2="36" stroke="#57534e" stroke-width="3" stroke-linecap="round"/>
<line x1="31" y1="31" x2="35" y2="35" stroke="#fbbf24" stroke-width="3" stroke-linecap="round"/>
<path d="M9 0c.8 5.3 3.7 8.2 9 9-5.3.8-8.2 3.7-9 9-.8-5.3-3.7-8.2-9-9 5.3-.8 8.2-3.7 9-9z" fill="#fde68a" stroke="#b45309" stroke-width=".9"/>
<path d="M21 2c.3 1.6 1 2.3 2.5 2.5-1.5.2-2.2.9-2.5 2.5-.3-1.6-1-2.3-2.5-2.5 1.5-.2 2.2-.9 2.5-2.5z" fill="#fff7ed"/>
<path d="M3 21c.2 1.2.8 1.8 2 2-1.2.2-1.8.8-2 2-.2-1.2-.8-1.8-2-2 1.2-.2 1.8-.8 2-2z" fill="#fff7ed"/>
</svg>`;
const wandCursor = `url("data:image/svg+xml,${encodeURIComponent(wandSvg)}") 9 9, pointer`;

// Pointer while "nachher" shows: a circular arrow back to the original; the click point is its centre (16 16).
const revertSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-3 -3 30 30" fill="none" stroke-linecap="round" stroke-linejoin="round">
<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5" stroke="#fff" stroke-width="5"/>
<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5" stroke="#1c1917" stroke-width="2.4"/>
</svg>`;
const revertCursor = `url("data:image/svg+xml,${encodeURIComponent(revertSvg)}") 16 16, pointer`;

// "Klicken Sie auf das Sofa …" on the before photo
const reveal = "clip-path 1.3s cubic-bezier(.4,0,.4,1)";

function method(p: Project) {
  return /traditionell|rosshaar|palmfaser/i.test(p.aufwand ?? "") ? "traditionell" : "modern";
}

function reduced() {
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Sparkles at (x, y) inside `layer` (w × h): a soft flash, a small burst and a trail along the growing circle.
function sparkle(layer: HTMLElement, x: number, y: number, w: number, h: number) {
  const reach = Math.max(Math.hypot(x, y), Math.hypot(w - x, y), Math.hypot(x, h - y), Math.hypot(w - x, h - y));
  const spark = (sx: number, sy: number, dist: number, delay: number, sz: number, ms: number) => {
    const el = document.createElement("span");
    const color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
    el.innerHTML = `<svg viewBox="0 0 24 24" width="${sz}" height="${sz}"><path fill="${color}" d="M12 0c.8 7 4.2 10.4 12 12-7.8 1.6-11.2 5-12 12-.8-7-4.2-10.4-12-12C7.8 10.4 11.2 7 12 0z"/></svg>`;
    Object.assign(el.style, {
      position: "absolute",
      left: `${sx}px`,
      top: `${sy}px`,
      display: "block",
      filter: `drop-shadow(0 0 4px ${color})`,
    });
    layer.appendChild(el);
    const a = Math.random() * 2 * Math.PI;
    const dx = Math.cos(a) * dist;
    const dy = Math.sin(a) * dist;
    const spin = (Math.random() - 0.5) * 360;
    el.animate(
      [
        { transform: "translate(-50%, -50%) scale(0) rotate(0deg)", opacity: 1 },
        { transform: `translate(calc(-50% + ${dx * 0.7}px), calc(-50% + ${dy * 0.7}px)) scale(1) rotate(${spin / 2}deg)`, opacity: 1, offset: 0.5 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.2) rotate(${spin}deg)`, opacity: 0 },
      ],
      { duration: ms, delay, easing: "cubic-bezier(.2,.7,.3,1)", fill: "both" },
    ).onfinish = () => el.remove();
  };
  const flash = document.createElement("span");
  Object.assign(flash.style, {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,247,237,.8), rgba(253,230,138,.35) 40%, transparent 70%)",
  });
  layer.appendChild(flash);
  flash.animate(
    [
      { transform: "translate(-50%, -50%) scale(.2)", opacity: 0.9 },
      { transform: "translate(-50%, -50%) scale(3.5)", opacity: 0 },
    ],
    { duration: 800, easing: "ease-out", fill: "both" },
  ).onfinish = () => flash.remove();
  for (let i = 0; i < 14; i++) spark(x, y, 40 + Math.random() * reach * 0.3, Math.random() * 120, 12 + Math.random() * 14, 700 + Math.random() * 400);
  for (let i = 0; i < 22; i++) {
    const t = Math.random();
    const a = Math.random() * 2 * Math.PI;
    spark(x + Math.cos(a) * t * reach, y + Math.sin(a) * t * reach, 12 + Math.random() * 24, t * 700, 9 + Math.random() * 10, 500 + Math.random() * 300);
  }
}

// The workshop tag swings a little on its string, around the point where the string meets the photo
// (top of .tag-wrap, 4 px right of centre, see .tag-string), settling back on its resting tilt of -1.2deg.
// The reveal gives it a livelier push to the right; going back a smaller, shorter nudge to the left.
const swings = {
  reveal: { degs: [-1.2, 4, -4.5, 2.5, -2.2, 0.4, -1.2], ms: 1500 },
  back: { degs: [-1.2, -4, 0.8, -2.6, -0.6, -1.2], ms: 1100 },
};

function swing(tag: HTMLElement | null | undefined, reveal: boolean) {
  if (!tag) return;
  const { degs, ms } = swings[reveal ? "reveal" : "back"];
  tag.animate(
    degs.map((deg) => ({ transform: `rotate(${deg}deg)` })),
    { duration: ms, easing: "ease-in-out" },
  );
}

// Zauberstab tool: the before photo; a click with the wand sets off sparkles at that spot and "nachher" grows out
// of it as a circle, another click shrinks it back. Keyboard starts from the middle. Nothing plays by itself:
// it waits for the click. startBefore: opens on the original (start page, gallery popup, project page);
// showTag: workshop tag under the photo (start page, popup). Several works (start page): "Nächstes Möbel".
export default function Wand({
  items,
  startBefore = false,
  showTag = false,
  side,
  chrome,
}: {
  items: Project[];
  startBefore?: boolean;
  showTag?: boolean;
  side?: { top: ReactNode; bottom: ReactNode };
  // side layout: px of the window height taken by everything around the photo (sizes the photo)
  chrome?: number;
}) {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(!startBefore);
  // has the visitor toggled at least once
  const [played, setPlayed] = useState(false);
  // false until hydrated: the CSS-only fallback runs meanwhile
  const [live, setLive] = useState(false);
  // circle centre in % and radius in px (0 = "covers from the middle", for the initial after state)
  const [at, setAt] = useState({ x: 50, y: 50, r: 0 });
  // true for a moment while the circle jumps without animation
  const [jump, setJump] = useState(false);
  // switching works: the old photo stays underneath for a moment while the new photo and note fade in
  const [leave, setLeave] = useState<string | null>(null);
  const [swapped, setSwapped] = useState(false);
  const frameRef = useRef<HTMLButtonElement>(null);
  const sparks = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);
  const tagRef = useRef<HTMLDivElement>(null);

  const p = items[index];
  const many = items.length > 1;
  const ratio = many ? 16 / 10 : p.ratio;

  const play = useCallback((to: boolean, px?: number, py?: number) => {
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = px ?? r.width / 2;
    const y = py ?? r.height * 0.55;
    const still = reduced();
    const reach = Math.max(Math.hypot(x, y), Math.hypot(r.width - x, y), Math.hypot(x, r.height - y), Math.hypot(r.width - x, r.height - y));
    // Move the centre first (no transition; the circle keeps covering or stays empty), then grow or shrink
    // it from there, so the animation starts at the click and doesn't slide over from the last one.
    setAt({ x: (x / r.width) * 100, y: (y / r.height) * 100, r: reach + 4 });
    setJump(true);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setJump(still);
        setShown(to);
        setPlayed(true);
      }),
    );
    // sparkles only for the reveal; going back to the original is a plain shrink
    if (to && !still && sparks.current) sparkle(sparks.current, x, y, r.width, r.height);
    if (!still) swing(tagRef.current?.querySelector<HTMLElement>(".tag-wrap"), to);
  }, []);

  // hydrated: drop the fallback; reduced motion or a late start (fallback already showed it) → after at once
  useEffect(() => {
    // the fallback animation already ran (slow hydration): keep the after photo
    const fade = afterRef.current?.getAnimations()[0];
    const late = Number(fade?.currentTime ?? 0) > 3000;
    const still = reduced();
    requestAnimationFrame(() => {
      setLive(true);
      if (startBefore && (still || late)) {
        setJump(true);
        setShown(true);
        setPlayed(true);
      }
    });
  }, [startBefore]);

  // load the next work's photos so "Nächstes Möbel" never waits
  useEffect(() => {
    if (!many) return;
    const n = items[(index + 1) % items.length];
    for (const id of [n.before, n.after]) if (id) new Image().src = d(id);
  }, [many, index, items]);

  function fade() {
    setLeave(d(shown ? p.after : before));
    setSwapped(true);
    setTimeout(() => setLeave(null), 700);
  }
  function prev() {
    fade();
    setIndex((i) => (i - 1 + items.length) % items.length);
    setJump(true);
    setShown(false);
    setPlayed(false);
    setAt({ x: 50, y: 50, r: 0 });
  }
  function next() {
    fade();
    setIndex((i) => (i + 1) % items.length);
    setJump(true);
    setShown(false);
    setPlayed(false);
    setAt({ x: 50, y: 50, r: 0 });
  }

  const before = p.before ?? p.after;
  const radius = shown ? (at.r ? `${at.r}px` : "150%") : "0px";
  const hints = {
    before: `So kam es zu uns.\nNeugierig auf das Ergebnis? Klicken Sie …`,
    after: "Original anzeigen?\nKlicken Sie nochmal.",
    first: "Wie sah es vorher aus?\nKlicken Sie aufs Foto.",
  };
  const hint = shown ? (played ? hints.after : hints.first) : hints.before;
  // Start page: the short tag; gallery popup (side): the full one, as on the former project page
  const rows = [
    { label: "Möbel", value: p.title },
    { label: "Alter", value: p.alter },
    ...(side ? [{ label: "Zustand", value: p.zustand }] : []),
    { label: "Arbeitszeit", value: p.hours },
    { label: "Methode", value: method(p) },
    ...(side
      ? [
          { label: "Material", value: p.materials },
          { label: "Bezug", value: p.cover },
        ]
      : []),
  ].filter((r): r is { label: string; value: string } => !!r.value);
  const pre = !live && !shown;

  // Hint: left-aligned so the arrow keeps its place whatever the text. Every text sits in one grid cell (only the
  // current one visible), so the box is as tall as the longest; all texts start at its top (two lines each), so
  // the arrow next to the first line never moves. Above the photo it points down; beside it (side) it points left.
  const down = (
    // from the first line down past the second to the photo
    <svg aria-hidden="true" width="48" height="70" viewBox="0 0 48 70" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", margin: 0, transform: "translateY(10px)" }}>
      <path d="M44 8C28 6 12 20 10 64" />
      <path d="M3 56l7 8 7-7" />
    </svg>
  );
  const left = (
    <svg aria-hidden="true" width="48" height="40" viewBox="0 0 48 40" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", margin: 0 }}>
      <path d="M46 26C34 8 18 6 5 14" />
      <path d="M13 6l-8 8 9 5" />
    </svg>
  );
  // chair layout: the hint sits left of the photo, so its arrow comes after the text and points right
  const right = (
    <svg aria-hidden="true" width="48" height="40" viewBox="0 0 48 40" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", margin: 0 }}>
      <path d="M2 26C14 8 30 6 43 14" />
      <path d="M35 6l8 8-9 5" />
    </svg>
  );
  const hintEl = (arrow: ReactNode, rtl = false) => (
    <p className="wand-hint text-accent" style={{ margin: side ? 0 : "0 0 12px 24px", justifyContent: rtl ? "flex-end" : "flex-start", textAlign: rtl ? "right" : undefined, position: "relative", zIndex: 5 }} aria-live="polite">
      <span className={`grid ${pre ? "wz-hint" : ""}`}>
        {Object.values(hints).map((t) => (
          <span
            key={t}
            className={`flex items-start gap-2.5 whitespace-pre-line [grid-area:1/1] ${rtl ? "flex-row-reverse" : ""} ${t === hint ? "" : "invisible"}`}
            aria-hidden={t !== hint}
          >
            {arrow}
            {/* chair layout: no forced line break; the text is centred in the (two-line high) box, the arrow stays at the top */}
            {rtl ? <span className="self-center">{t.replace("\n", " ")}</span> : t}
          </span>
        ))}
      </span>
    </p>
  );

  const photo = (
      <button
        ref={frameRef}
        type="button"
        className="wz"
        aria-pressed={shown}
        aria-label="Vorher/Nachher zeigen"
        style={{ cursor: shown ? revertCursor : wandCursor }}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          // detail 0 = keyboard: start from the middle
          play(!shown, e.detail ? e.clientX - r.left : undefined, e.detail ? e.clientY - r.top : undefined);
        }}
      >
        <figure
          className="wand-fig relative m-0 select-none overflow-hidden rounded-xl bg-muted shadow-[0_2px_4px_rgba(60,40,20,.10),0_14px_28px_-8px_rgba(60,40,20,.28),0_36px_64px_-24px_rgba(60,40,20,.28)] ring-1 ring-black/5"
          style={{ aspectRatio: String(ratio) }}
        >
          {leave && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={leave} alt="" aria-hidden="true" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            key={`b-${p.slug}`}
            src={d(before)}
            {...size(before)}
            alt={`${p.title}, vorher`}
            fetchPriority={startBefore ? "high" : undefined}
            loading="eager"
            draggable={false}
            className={`absolute inset-0 h-full w-full object-cover ${swapped ? "wz-in" : ""}`}
          />
          <div
            ref={afterRef}
            className="wz-after absolute inset-0"
            style={{ clipPath: `circle(${radius} at ${at.x}% ${at.y}%)`, transition: jump ? undefined : reveal }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              key={`a-${p.slug}`}
              src={d(p.after)}
              {...size(p.after)}
              alt={`${p.title}, nachher`}
              fetchPriority={startBefore ? undefined : "high"}
              loading="eager"
              draggable={false}
              className="h-full w-full object-cover"
            />
          </div>
          <div ref={sparks} aria-hidden="true" className="pointer-events-none absolute inset-0" />
          <span className="wand-tape ttag-w" aria-hidden="true">
            <span className="ttag">
              {pre ? (
                <span className="ttag-v wz-swap">
                  <span className="wz-b">Vorher</span>
                  <span className="wz-a">Nachher</span>
                </span>
              ) : (
                // both words in one cell, cross-fading with the photo
                <span className="ttag-v wz-swap">
                  <span className={`transition-opacity duration-500 motion-reduce:transition-none ${shown ? "opacity-0" : "opacity-100"}`}>Vorher</span>
                  <span className={`transition-opacity duration-500 motion-reduce:transition-none ${shown ? "opacity-100" : "opacity-0"}`}>Nachher</span>
                </span>
              )}
            </span>
          </span>
        </figure>
      </button>
  );

  // previous / next: round arrow buttons; `pos` places the pair, `l` / `r` the sides
  const arrows = (pos: string, l: string, r: string) =>
    (["prev", "next"] as const).map((dir) => (
      <button
        key={dir}
        type="button"
        onClick={dir === "prev" ? prev : next}
        aria-label={dir === "prev" ? "Vorheriges Möbel" : "Nächstes Möbel"}
        className={`absolute z-10 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-line-strong bg-bg/80 text-ink backdrop-blur-sm hover:bg-bg hover:text-accent ${pos} ${dir === "prev" ? l : r}`}
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d={dir === "prev" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
        </svg>
      </button>
    ));

  // Side layout (gallery popup): the photo large on the left, sized to the window height, the caller's bottom
  // (text, buttons) under it; on the right the caller's top (title), the hint pointing left and the workshop tag.
  // Phones: stacked (top, hint, photo, tag, bottom).
  if (side) {
    // Upright photo (chairs), from 1280 px: two columns. Left: title, hint (arrow points right at the photo) and tag, flush right; right: the photo at the
    // column's left edge (so the gap between the columns sits at the centre of the popup), the text below it from the same edge, running wider than the photo.
    const upright = ratio < 0.9;
    const u = (c: string) => (upright ? c : "");
    return (
      <div
        className={`relative flex flex-col gap-5 min-[900px]:grid min-[900px]:grid-cols-[minmax(0,max(var(--wz-max),520px))_400px] min-[900px]:grid-rows-[auto_1fr] min-[900px]:items-start min-[900px]:justify-center min-[900px]:gap-x-10 ${u(
          "min-[1280px]:grid-cols-2 min-[1280px]:justify-stretch min-[1280px]:gap-x-10"
        )} ${pre ? "wand-pre" : ""}`}
        style={{ "--wz-max": `calc((100dvh - ${chrome ?? (upright ? 370 : 340)}px) * ${ratio})` } as CSSProperties}
      >
        {many && <div className="hidden min-[1360px]:contents">{arrows("top-1/2", "-left-14", "-right-14")}</div>}
        <div className={`contents min-[900px]:col-start-1 min-[900px]:row-span-2 min-[900px]:row-start-1 min-[900px]:flex min-[900px]:flex-col min-[900px]:gap-5 ${u("min-[1280px]:col-start-2 min-[1280px]:w-full min-[1280px]:max-w-[calc(var(--wz-max)_+_320px)] min-[1280px]:justify-self-start")}`}>
          <div className="relative order-3 w-full min-[900px]:max-w-[var(--wz-max)]">
            {photo}
            {many && arrows("min-[1360px]:hidden top-1/2", "left-3", "right-3")}
          </div>
          <div className="order-5">{side.bottom}</div>
        </div>
        <div className={`contents min-[900px]:col-start-2 min-[900px]:row-span-2 min-[900px]:row-start-1 min-[900px]:flex min-[900px]:flex-col min-[900px]:gap-5 ${u("min-[1280px]:col-start-1 min-[1280px]:w-[400px] min-[1280px]:max-w-full min-[1280px]:self-center min-[1280px]:justify-self-end")}`}>
          <div className={`order-1 ${u("min-[1280px]:w-max min-[1280px]:min-w-full min-[1280px]:max-w-[calc(100%_+_180px)] min-[1280px]:self-end min-[1280px]:text-right min-[1280px]:[&>*]:!pr-5")}`}>{side.top}</div>
          <div className="order-2 min-[900px]:hidden">{hintEl(down)}</div>
          {/* narrow photo: the column beside it is wider than the photo, so the hint reaches into the free space */}
          <div className={`order-2 hidden min-[900px]:ml-[calc(-1*max(0px,_536px_-_var(--wz-max)))] min-[900px]:block ${u("min-[1280px]:hidden")}`}>{hintEl(left)}</div>
          {/* sticks out to the left (as far as the popup has room) so the text fits two rows */}
          {upright && (
            <div className="order-2 hidden min-[1280px]:ml-[max(-200px,calc(-1*(100vw_-_1280px)/2_-_130px))] min-[1280px]:block min-[1280px]:[&_.wand-hint]:!text-[26px]">
              {hintEl(right, true)}
            </div>
          )}
          {/* several works: room for the tallest note, so the page below does not jump when switching */}
          <div ref={tagRef} className={`wz-tag wz-tag-side order-4 ${many ? "min-[900px]:min-h-[540px]" : ""}`}>
            <WorkshopTag key={p.slug} rows={rows} className="wand-tag" write={swapped || many} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pre ? "wand-pre" : undefined}>
      {hintEl(down)}
      {photo}
      <div className="wand-foot">
        {showTag && (
          <div ref={tagRef} className="wz-tag">
            <WorkshopTag rows={rows} className="wand-tag" />
          </div>
        )}
        {/* equal spacing photo → link → link; the ::after stretches each tap area to 44 px */}
        <div className="flex flex-[1_1_220px] flex-col items-end pt-3">
          {many && (
            <div className="flex flex-col items-end gap-2">
              {many && (
                <button
                  type="button"
                  onClick={next}
                  className="cursor-pointer relative py-1 leading-tight font-semibold text-accent after:absolute after:inset-x-0 after:-inset-y-1.5 hover:underline"
                >
                  Nächstes Möbel →
                </button>
              )}
              {many && (
                <Link href="/polsterei/" className="relative py-1 leading-tight font-semibold text-accent after:absolute after:inset-x-0 after:-inset-y-1.5 hover:underline">
                  Alle Beispiele →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
